import { useState, useEffect, useRef, useCallback } from 'react';
import { prospectsAPI } from '@/lib/api';
import { WallItemDialog } from './WallItemDialog';
import { HashtagPicker } from './HashtagPicker';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { AtSign, Send, Loader2, MessageSquareText, Trash2, Zap, HardHat, CheckSquare, Users, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const initials = (name) => {
    if (!name) return '?';
    const p = name.trim().split(/\s+/);
    return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
};

// 6 réactions standards « façon Facebook »
const REACTIONS = [
    { key: 'like', emoji: '👍', label: "J'aime" },
    { key: 'love', emoji: '❤️', label: 'Cœur' },
    { key: 'haha', emoji: '😆', label: 'Haha' },
    { key: 'wow', emoji: '😮', label: 'Waouh' },
    { key: 'sad', emoji: '😢', label: 'Triste' },
    { key: 'clap', emoji: '👏', label: 'Bravo' },
];

// Détail des réactions : QUI a réagi + AVEC QUELLE réaction.
// S'ouvre au survol (desktop) ET au tap (mobile, priorité). Overlay pour fermer au tap extérieur.
const ReactionsViewer = ({ summary, noteId }) => {
    const [open, setOpen] = useState(false);
    const items = [];
    REACTIONS.forEach((r) => {
        const detail = summary?.reactors_detail?.[r.key];
        if (detail && detail.length) {
            detail.forEach((d) => items.push({ name: d.name, avatar: d.avatar, emoji: r.emoji, label: r.label }));
        } else {
            // Repli si reactors_detail absent (ancien payload) : noms seuls
            (summary?.reactors?.[r.key] || []).forEach((name) =>
                items.push({ name, avatar: null, emoji: r.emoji, label: r.label })
            );
        }
    });
    if (items.length === 0) return null;
    return (
        <div
            className="relative"
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(true); }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpen(false); }}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid={`wall-reactions-who-${noteId}`}
                className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-purple-600 transition-colors"
                title="Voir qui a réagi"
            >
                <Users className="w-3.5 h-3.5" />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
                    <div
                        data-testid={`wall-reactions-popover-${noteId}`}
                        className="absolute z-40 bottom-full mb-1 left-0 min-w-[200px] max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                    >
                        <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            Qui a réagi
                        </p>
                        {items.map((it, i) => (
                            <div key={i} className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700">
                                <span className="relative shrink-0">
                                    <Avatar className="h-6 w-6">
                                        {it.avatar && <AvatarImage src={it.avatar} />}
                                        <AvatarFallback className="text-[9px] bg-slate-100 text-slate-600">{initials(it.name)}</AvatarFallback>
                                    </Avatar>
                                    <span className="absolute -bottom-1 -right-1 text-[10px] leading-none">{it.emoji}</span>
                                </span>
                                <span className="flex-1 truncate">{it.name}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

// Mur du dossier — indépendant par module (Easy Power / Chantier).
// Tout intervenant du dossier peut écrire. Messages du plus récent au plus ancien.
export default function ProspectWall({ prospectId, module, moduleLabel, onRead }) {
    const { user, refreshWallUnread } = useAuth();
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;

    const [notes, setNotes] = useState([]);
    const [users, setUsers] = useState([]);
    const [content, setContent] = useState('');
    const [mentioned, setMentioned] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [pickerFor, setPickerFor] = useState(null);

    const [showSuggest, setShowSuggest] = useState(false);
    const [query, setQuery] = useState('');
    const [caretAt, setCaretAt] = useState(null);
    const taRef = useRef(null);
    const [wallItem, setWallItem] = useState(null); // { refType, refId } — tâche/événement ouvert depuis le mur

    const accent = module === 'chantier' ? 'blue' : 'amber';
    const Icon = module === 'chantier' ? HardHat : Zap;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [nRes, uRes] = await Promise.all([
                prospectsAPI.getNotes(prospectId, module),
                prospectsAPI.getMentionableUsers(prospectId, module),
            ]);
            setNotes(nRes.data || []);
            setUsers(uRes.data || []);
        } catch (e) {
            console.error('wall load error', e);
        } finally {
            setLoading(false);
        }
    }, [prospectId, module]);

    useEffect(() => { load(); }, [load]);

    // Ouvrir le mur d'un module = le marquer comme lu (efface la pastille de cet onglet)
    useEffect(() => {
        prospectsAPI.markWallRead(prospectId, module)
            .then(() => { onRead?.(module); refreshWallUnread?.(); })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prospectId, module]);

    const onChange = (e) => {
        const val = e.target.value;
        setContent(val);
        const pos = e.target.selectionStart;
        const upto = val.slice(0, pos);
        const match = upto.match(/@([\p{L}\-]*)$/u);
        if (match) {
            setCaretAt(pos - match[0].length);
            setQuery(match[1].toLowerCase());
            setShowSuggest(true);
        } else {
            setShowSuggest(false);
            setQuery('');
            setCaretAt(null);
        }
    };

    const filtered = users.filter((u) => !query || (u.name || '').toLowerCase().includes(query)).slice(0, 6);

    const pickUser = (u) => {
        if (caretAt == null) return;
        const before = content.slice(0, caretAt);
        const after = content.slice(caretAt + 1 + query.length);
        const next = `${before}@${u.name} ${after}`;
        setContent(next);
        setShowSuggest(false);
        setQuery('');
        setCaretAt(null);
        if (!mentioned.some((m) => m.id === u.id)) setMentioned((prev) => [...prev, u]);
        setTimeout(() => taRef.current?.focus(), 0);
    };

    const removeMention = (id) => setMentioned((prev) => prev.filter((m) => m.id !== id));

    const submit = async () => {
        if (!content.trim()) return;
        const stillMentioned = mentioned.filter((m) => content.includes(`@${m.name}`));
        setSaving(true);
        try {
            await prospectsAPI.addNote(prospectId, {
                content: content.trim(),
                mentioned_user_ids: stillMentioned.map((m) => m.id),
                module,
            });
            toast.success('Message publié');
            setContent('');
            setMentioned([]);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de la publication");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (noteId) => {
        if (!confirm('Supprimer ce message du mur ?')) return;
        setDeletingId(noteId);
        try {
            await prospectsAPI.deleteNote(prospectId, noteId);
            setNotes((prev) => prev.filter((n) => n.id !== noteId));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeletingId(null);
        }
    };

    const handleReact = async (noteId, reaction) => {
        setPickerFor(null);
        // Optimiste : recharger la synthèse renvoyée par l'API
        try {
            const res = await prospectsAPI.reactToNote(prospectId, noteId, reaction);
            setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, reactions_summary: res.data } : n)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la réaction');
        }
    };

    const renderContent = (text, names) => {
        if (!names || names.length === 0) return text;
        const sorted = [...names].sort((a, b) => b.length - a.length);
        const regex = new RegExp(`@(${sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
        const parts = [];
        let lastIndex = 0;
        let key = 0;
        let m;
        while ((m = regex.exec(text)) !== null) {
            if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
            parts.push(<span key={key++} className="text-purple-700 font-semibold bg-purple-50 rounded px-1">{m[0]}</span>);
            lastIndex = m.index + m[0].length;
        }
        if (lastIndex < text.length) parts.push(text.slice(lastIndex));
        return parts;
    };

    return (
        <Card data-testid={`wall-${module}`} className={`border-${accent}-200`}>
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquareText className={`w-5 h-5 text-${accent}-600`} />
                    Mur du dossier
                    <Badge variant="outline" className="ml-1 gap-1 text-[11px]">
                        <Icon className="w-3 h-3" />{moduleLabel}
                    </Badge>
                </CardTitle>
                <p className="text-xs text-slate-500">
                    Espace d'échange visible par tous les intervenants de ce dossier. Tapez{' '}
                    <span className="font-mono text-purple-600">@</span> pour mentionner et notifier quelqu'un.
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Hashtags # du module (tout le monde peut taguer ; # créés par les admins) */}
                <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2">
                    <span className="text-[11px] font-medium text-slate-500 shrink-0">Hashtags :</span>
                    <HashtagPicker prospectId={prospectId} module={module} />
                </div>
                {/* Composer */}
                <div className="relative">
                    <textarea
                        ref={taRef}
                        value={content}
                        onChange={onChange}
                        placeholder="Écrire un message sur le mur…  (@ pour mentionner)"
                        rows={2}
                        data-testid={`wall-composer-${module}`}
                        className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-300"
                    />
                    {showSuggest && filtered.length > 0 && (
                        <div data-testid={`wall-mention-suggestions-${module}`}
                            className="absolute z-20 mt-1 w-72 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                            {filtered.map((u) => (
                                <button key={u.id} type="button" data-testid={`wall-mention-option-${u.id}`}
                                    onClick={() => pickUser(u)}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-purple-50">
                                    <Avatar className="h-6 w-6">
                                        {u.avatar && <AvatarImage src={u.avatar} />}
                                        <AvatarFallback className="bg-purple-100 text-purple-700 text-[10px]">{initials(u.name)}</AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm text-slate-700">{u.name}</span>
                                    <Badge variant="outline" className="ml-auto text-[10px] capitalize">{u.role}</Badge>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {mentioned.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {mentioned.map((m) => (
                            <Badge key={m.id} className="bg-purple-100 text-purple-700 gap-1 hover:opacity-90">
                                <AtSign className="w-3 h-3" />{m.name}
                                <button type="button" onClick={() => removeMention(m.id)} className="ml-1 hover:text-purple-900">×</button>
                            </Badge>
                        ))}
                    </div>
                )}

                <div className="flex justify-end">
                    <Button onClick={submit} disabled={saving || !content.trim()} size="sm" data-testid={`wall-submit-${module}`}
                        className="bg-purple-600 hover:bg-purple-700 gap-1">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Publier
                    </Button>
                </div>

                {/* Fil — plus récent en haut, scrollable */}
                <div className="space-y-3 pt-2 border-t border-slate-100 max-h-96 overflow-y-auto pr-1" data-testid={`wall-thread-${module}`}>
                    {loading ? (
                        <p className="text-sm text-slate-400 text-center py-4">Chargement…</p>
                    ) : notes.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">Aucun message pour le moment. Lancez la discussion !</p>
                    ) : (
                        notes.map((n) => {
                            const canDelete = isAdmin || n.author_id === user?.id;
                            const isTaskEvent = n.kind === 'task_event';
                            const isCalEvent = n.kind === 'event_event';
                            const isSystem = isTaskEvent || isCalEvent;
                            return (
                                <div key={n.id} className={`flex gap-3 group ${isTaskEvent ? 'rounded-lg border border-amber-200 bg-amber-50/60 p-2' : isCalEvent ? 'rounded-lg border border-blue-200 bg-blue-50/60 p-2' : ''}`} data-testid={`wall-msg-${n.id}`}>
                                    <Avatar className="h-8 w-8 shrink-0">
                                        {n.author_avatar && <AvatarImage src={n.author_avatar} />}
                                        <AvatarFallback className="bg-slate-100 text-slate-600 text-[11px]">{initials(n.author_name)}</AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-sm font-semibold text-slate-800">{n.author_name}</span>
                                            {isTaskEvent && (
                                                <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1" data-testid={`wall-task-badge-${n.id}`}>
                                                    <CheckSquare className="w-3 h-3" />Tâche
                                                </Badge>
                                            )}
                                            {isCalEvent && (
                                                <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1" data-testid={`wall-event-badge-${n.id}`}>
                                                    <CalendarClock className="w-3 h-3" />Événement
                                                </Badge>
                                            )}
                                            <span className="text-xs text-slate-400" data-testid={`wall-msg-date-${n.id}`}>{fmtDateTime(n.created_at)}</span>
                                            {canDelete && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(n.id)}
                                                    disabled={deletingId === n.id}
                                                    data-testid={`wall-delete-${n.id}`}
                                                    className="ml-auto text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                                    title="Supprimer"
                                                >
                                                    {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                                </button>
                                            )}
                                        </div>
                                        {isSystem && n.ref_id ? (
                                            <button
                                                type="button"
                                                onClick={() => setWallItem({ refType: n.ref_type, refId: n.ref_id })}
                                                data-testid={`wall-open-item-${n.id}`}
                                                className="block w-full text-left text-sm text-slate-700 whitespace-pre-wrap mt-0.5 hover:underline decoration-dotted underline-offset-4 cursor-pointer"
                                                title={n.ref_type === 'event' ? "Ouvrir l'événement" : 'Ouvrir la tâche'}
                                            >
                                                {n.content}
                                                <span className="ml-1 text-[11px] font-medium text-blue-600">— ouvrir ›</span>
                                            </button>
                                        ) : (
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{renderContent(n.content, n.mentioned_names)}</p>
                                        )}
                                        {n.mentioned_names?.length > 0 && (
                                            <p className="text-[11px] text-slate-400 mt-1">Mentionné(s) par email : {n.mentioned_names.join(', ')}</p>
                                        )}
                                        {/* Barre de réactions « façon Facebook » */}
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setPickerFor(pickerFor === n.id ? null : n.id)}
                                                    data-testid={`wall-react-toggle-${n.id}`}
                                                    className="text-[11px] text-slate-400 hover:text-purple-600 transition-colors flex items-center gap-1"
                                                    title="Réagir"
                                                >
                                                    <span className="text-sm">🙂</span> Réagir
                                                </button>
                                                {pickerFor === n.id && (
                                                    <div
                                                        data-testid={`wall-react-picker-${n.id}`}
                                                        className="absolute z-30 bottom-full mb-1 left-0 flex gap-0.5 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-lg"
                                                    >
                                                        {REACTIONS.map((r) => (
                                                            <button
                                                                key={r.key}
                                                                type="button"
                                                                onClick={() => handleReact(n.id, r.key)}
                                                                data-testid={`wall-react-${r.key}-${n.id}`}
                                                                title={r.label}
                                                                className={`text-lg leading-none p-1 rounded-full transition-transform hover:scale-125 ${n.reactions_summary?.my === r.key ? 'bg-purple-100' : 'hover:bg-slate-100'}`}
                                                            >
                                                                {r.emoji}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Pastilles agrégées des réactions existantes */}
                                            {n.reactions_summary?.total > 0 && (
                                                <div className="flex items-center gap-1 flex-wrap" data-testid={`wall-reactions-${n.id}`}>
                                                    {REACTIONS.filter((r) => n.reactions_summary.counts?.[r.key]).map((r) => {
                                                        const reactors = n.reactions_summary.reactors?.[r.key] || [];
                                                        const detail = n.reactions_summary.reactors_detail?.[r.key] || [];
                                                        const shown = detail.slice(0, 3);
                                                        const extra = detail.length - shown.length;
                                                        return (
                                                            <button
                                                                key={r.key}
                                                                type="button"
                                                                onClick={() => handleReact(n.id, r.key)}
                                                                data-testid={`wall-reaction-pill-${r.key}-${n.id}`}
                                                                title={reactors.join(', ')}
                                                                className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] transition-colors ${n.reactions_summary.my === r.key ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                                                            >
                                                                <span className="text-xs">{r.emoji}</span>
                                                                {shown.length > 0 && (
                                                                    <span className="flex items-center -space-x-1.5">
                                                                        {shown.map((d, di) => (
                                                                            <Avatar key={di} className="h-4 w-4 ring-1 ring-white">
                                                                                {d.avatar && <AvatarImage src={d.avatar} />}
                                                                                <AvatarFallback className="text-[7px] bg-slate-200 text-slate-600">{initials(d.name)}</AvatarFallback>
                                                                            </Avatar>
                                                                        ))}
                                                                    </span>
                                                                )}
                                                                <span>{extra > 0 ? `+${extra}` : n.reactions_summary.counts[r.key]}</span>
                                                            </button>
                                                        );
                                                    })}
                                                    <ReactionsViewer summary={n.reactions_summary} noteId={n.id} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </CardContent>

            <WallItemDialog
                open={!!wallItem}
                refType={wallItem?.refType}
                refId={wallItem?.refId}
                onClose={() => setWallItem(null)}
                onChanged={load}
            />
        </Card>
    );
}
